"use client";

import { useLocale, useTranslations } from "next-intl";
import NextImage, { type ImageProps as NextImageProps } from "next/image";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";

import { resolveImageProps } from "@/app/utilities/images";
import { mapTagColor } from "@/app/utilities/mapTagColor";
import type { OfferReference } from "@/types/references";

import {
	FeaturedOfferCard,
	type FeaturedOfferCardProps
} from "./FeaturedOfferCard";
import type { FeaturedCardBlockProps } from "./FeaturedCard";

export interface FeaturedCardContentProps extends FeaturedCardBlockProps {
	offerData?: OfferReference;
}

/**
 * Maps a resolved `featured_card` module + its `OfferReference` entry to the
 * `FeaturedOfferCard` composite.
 */
export function FeaturedCardContent({
	offerData,
	image
}: FeaturedCardContentProps): JSX.Element {
	const locale = useLocale();
	const t = useTranslations("featuredOfferCard");
	const tBadge = useTranslations("promotionalBadge");
	const [tagColor, tagLabel] = offerData?.offer_type?.split("|") ?? [
		"purple",
		""
	];
	const ctas = offerData?.call_to_action?.slice(0, 2).map((cta) => ({
		component: NextLink,
		children: cta.link.title,
		href: cta.link.href,
		target: cta.open_new_tab ? "_blank" : undefined
	}));
	const modalData = offerData?.details_and_condition_modal;
	const formattedDeadline = offerData?.deadline
		? new Date(offerData.deadline).toLocaleString(locale, {
				month: "long",
				day: "2-digit"
			})
		: undefined;
	const promotionalBadge =
		offerData?.coupon?.coupon_code || offerData?.coupon?.description
			? {
					promoCode: offerData?.coupon?.coupon_code ?? "",
					text: offerData?.coupon?.description ?? "",
					copyLabel: tBadge("copyLabel"),
					confirmationLabel: tBadge("confirmationLabel")
				}
			: undefined;

	return (
		<FeaturedOfferCard<NextImageProps, NextLinkProps>
			audienceTag={{
				kind: "solid",
				size: "md",
				color: mapTagColor(
					tagColor
				) as FeaturedOfferCardProps["audienceTag"]["color"],
				label: tagLabel
			}}
			cardTitle={{
				tag: offerData?.offer_title.headline_tag,
				children: offerData?.offer_title.headline
			}}
			cardDescription={offerData?.description ?? ""}
			promotionalBadge={promotionalBadge}
			badgeText={
				formattedDeadline
					? t("badgeDeadline", { deadline: formattedDeadline })
					: undefined
			}
			badgeCtaLabel={modalData?.modal_title.headline}
			badgeCtaModal={{
				title: modalData?.modal_title.headline ?? "",
				titleTag: modalData?.modal_title.headline_tag,
				description:
					offerData?.details_and_condition_modal.description ?? ""
			}}
			ctas={
				ctas as FeaturedOfferCardProps<
					NextImageProps,
					NextLinkProps
				>["ctas"]
			}
			image={{
				...resolveImageProps("16:9", image),
				component: NextImage,
				priority: true
			}}
		/>
	);
}
